// src/App.js
import React, { Suspense, useEffect } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { theme } from './shared/theme/theme';
import { GameProvider, useGameContext } from './shared/context/GameContext';
import { LoginScreen } from './app/LoginScreen';
import { HomeScreen } from './app/HomeScreen';
import { AudioFab } from './app/AudioControls';
import { ScreenLoader, SplashScreen } from './app/Shell';
import { useRoomAudioCues } from './shared/hooks/useGameSession';
import { setMusicScene } from './shared/audio/gameAudio';
import { GAME_STATE_KEYS } from './shared/GameEngine';
import { lazyNamed } from './shared/utils';
import { Box, CircularProgress, Typography } from '@mui/material';

// Each game, the lobby and the voice client (LiveKit) load on demand so the
// home screen's first download stays small.
const Lobby = lazyNamed(() => import('./features/multiplayer/Lobby'), 'Lobby');
const GameVoiceChat = lazyNamed(() => import('./features/multiplayer/GameVoiceChat'), 'GameVoiceChat');
const DrawingGame = lazyNamed(() => import('./features/multiplayer/drawing/DrawingGame'), 'DrawingGame');
const LudoGame = lazyNamed(() => import('./features/multiplayer/ludo/LudoGame'), 'LudoGame');
const SnakeLadderGame = lazyNamed(() => import('./features/multiplayer/snakeladder/SnakeLadderGame'), 'SnakeLadderGame');
const UnoGame = lazyNamed(() => import('./features/multiplayer/uno/UnoGame'), 'UnoGame');
const MiniGolfGame = lazyNamed(() => import('./features/multiplayer/minigolf/MiniGolfGame'), 'MiniGolfGame');
const QuizGame = lazyNamed(() => import('./features/multiplayer/quiz/QuizGame'), 'QuizGame');
const BoggleGame = lazyNamed(() => import('./features/multiplayer/boggle/BoggleGame'), 'BoggleGame');

const GAME_COMPONENTS = {
  drawing: DrawingGame,
  ludo: LudoGame,
  snakeladder: SnakeLadderGame,
  uno: UnoGame,
  minigolf: MiniGolfGame,
  quiz: QuizGame,
  boggle: BoggleGame,
};

function AppContent() {
  const { state } = useGameContext();
  const { isAuthReady, isResolvingUser, isLoggedIn, roomId, room } = state;
  useRoomAudioCues(roomId, room);

  // In a lobby or match the multiplayer theme plays; the home screen sets its own per section.
  const inRoom = !!roomId && !!room;
  useEffect(() => {
    if (inRoom) setMusicScene('multiplayer');
  }, [inRoom]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    // Warm the lobby chunk once the home screen is idle so joining a room feels instant.
    const schedule = window.requestIdleCallback || (callback => setTimeout(callback, 1500));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const handle = schedule(() => { import('./features/multiplayer/Lobby'); });
    return () => cancel(handle);
  }, [isLoggedIn]);

  // Splash until Firebase has reported the sign-in state and a signed-in user's profile is ready,
  // so a returning user never sees the login page flash.
  if (!isAuthReady) return <SplashScreen message="Checking your session…" />;
  if (isResolvingUser) return <SplashScreen message="Signing you in…" />;

  if (!isLoggedIn) {
    return (
      <motion.div key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ height: '100dvh' }}>
        <LoginScreen />
      </motion.div>
    );
  }

  const screen = !roomId ? 'home'
    : !room ? 'home'
    : room.status === 'waiting' ? 'lobby'
    : 'game';

  const GameComp = GAME_COMPONENTS[room?.gameType] || DrawingGame;
  const stateKey = GAME_STATE_KEYS[room?.gameType];
  const isGameStateReady = !!room && (stateKey ? !!room[stateKey] : true);

  return (
    <>
      <AudioFab>
        {roomId && room && (
          <Suspense fallback={null}>
            <GameVoiceChat key={roomId} roomId={roomId} docked />
          </Suspense>
        )}
      </AudioFab>
      <AnimatePresence mode="wait">
        {screen === 'home' && (
          <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            <HomeScreen />
          </motion.div>
        )}
        {screen === 'lobby' && (
          <motion.div key="lobby" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            <Suspense fallback={<ScreenLoader />}>
              <Lobby />
            </Suspense>
          </motion.div>
        )}
        {screen === 'game' && (
          <motion.div key="game" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} style={{ height: '100dvh' }}>
            {isGameStateReady ? (
              <Suspense fallback={<ScreenLoader />}>
                <GameComp />
              </Suspense>
            ) : (
              <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'var(--app-background)', color: 'white' }}>
                <CircularProgress sx={{ color: 'var(--app-primary)', mb: 2 }} />
                <Typography sx={{ fontWeight: 700, color: 'var(--app-text-secondary)' }}>Loading game state...</Typography>
              </Box>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GameProvider>
        <AppContent />
      </GameProvider>
    </ThemeProvider>
  );
}
